import { createQueryKeys } from '@lukemorales/query-key-factory';

import type {
  FetchStatsBreakdownArgs,
  FinanceStatsRangeArgs,
} from '../api/types/payments.types';

import {
  fetchStatsEarningsByTrainer,
  fetchStatsPlatformRevenue,
  fetchStatsReconciliation,
  fetchStatsSubscriptions,
  fetchStatsMarketplace,
  fetchStatsBreakdown,
  fetchStatsExpenses,
  fetchStatsOverview,
} from '../api/financeStats.api';

/**
 * The currency selector (T6) travels inside the shared range arguments, but
 * only `breakdown` sends it to the server — every other section filters rows it
 * already has. Leaving it in their keys would cache one IDENTICAL response per
 * currency and refetch on every selection, so it is stripped here, once, for
 * both the key and the query function.
 */
function withoutCurrency(args: FinanceStatsRangeArgs): FinanceStatsRangeArgs {
  if (args.currency === undefined) return args;
  const rest = { ...args };
  delete rest.currency;
  return rest;
}

export const financeStatsQueryKeys = createQueryKeys('financeStats', {
  // the ONE key that keeps `currency`: the rows come back already filtered
  fetchBreakdown: (args: FetchStatsBreakdownArgs) => ({
    queryFn: () => fetchStatsBreakdown(args),
    queryKey: [args],
  }),
  fetchEarningsByTrainer: (args: FinanceStatsRangeArgs) => ({
    queryFn: () => fetchStatsEarningsByTrainer(withoutCurrency(args)),
    queryKey: [withoutCurrency(args)],
  }),
  // The BE now buckets byMonth in the ORGANIZATION's own zone (Foundation 1,
  // D6/D7), not the viewer's — so the viewer's zone is no longer part of the
  // cache key.
  fetchExpenses: (args: FinanceStatsRangeArgs) => ({
    queryFn: () => fetchStatsExpenses(withoutCurrency(args)),
    queryKey: [withoutCurrency(args)],
  }),
  fetchMarketplace: (args: FinanceStatsRangeArgs) => ({
    queryFn: () => fetchStatsMarketplace(withoutCurrency(args)),
    queryKey: [withoutCurrency(args)],
  }),
  fetchOverview: (args: FinanceStatsRangeArgs) => ({
    queryFn: () => fetchStatsOverview(withoutCurrency(args)),
    queryKey: [withoutCurrency(args)],
  }),
  fetchPlatformRevenue: (args: FinanceStatsRangeArgs) => ({
    queryFn: () => fetchStatsPlatformRevenue(withoutCurrency(args)),
    queryKey: [withoutCurrency(args)],
  }),
  fetchReconciliation: (args: FinanceStatsRangeArgs) => ({
    queryFn: () => fetchStatsReconciliation(withoutCurrency(args)),
    queryKey: [withoutCurrency(args)],
  }),
  fetchSubscriptions: (args: FinanceStatsRangeArgs) => ({
    queryFn: () => fetchStatsSubscriptions(withoutCurrency(args)),
    queryKey: [withoutCurrency(args)],
  }),
});
